﻿import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import {
  Bell, CheckCircle2, Eye, AlertTriangle, BookOpen,
  Calendar, Clock, Users, Loader2,
} from 'lucide-react';
import type { ApiError, NoticeWithMeta, NoticeType, NoticePriority } from '@pms/shared';
import { noticeService } from '@/services/notice.service';
import { StatCard } from '@/components/ui/stat-card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs } from '@/components/ui/tabs';
import { SearchInput } from '@/components/ui/search-input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { RichTextContent } from '@/components/ui/rich-text-content';
import { formatDate, formatDateTime } from '@/lib/dateFormat';

function errMsg(e: unknown): string {
  return (e as AxiosError<ApiError>).response?.data?.error?.message ?? 'Something went wrong';
}

const PRIORITY_STYLES: Record<NoticePriority, string> = {
  critical: 'bg-rose-100 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-400 dark:border-rose-900/50',
  high:     'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-900/50',
  normal:   'bg-teal-100 text-teal-700 border-teal-200 dark:bg-teal-900/30 dark:text-teal-400 dark:border-teal-900/50',
  low:      'bg-gray-100 text-gray-500 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700',
};

const PRIORITY_LABELS: Record<NoticePriority, string> = {
  critical: 'Critical', high: 'High', normal: 'Normal', low: 'Low',
};

const TYPE_STYLES: Record<NoticeType, string> = {
  urgent:   'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/20 dark:text-rose-400 dark:border-rose-900/40',
  policy:   'bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-900/20 dark:text-violet-400 dark:border-violet-900/40',
  event:    'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-900/40',
  circular: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-900/40',
  general:  'bg-teal-50 text-teal-700 border-teal-200 dark:bg-teal-900/20 dark:text-teal-400 dark:border-teal-900/40',
};

const TYPE_LABELS: Record<NoticeType, string> = {
  urgent: 'Urgent', policy: 'Policy', event: 'Event', circular: 'Circular', general: 'General',
};

const ALL_TABS = ['all', 'unread', 'acknowledged', 'pending_ack'] as const;
type TabKey = (typeof ALL_TABS)[number];

const TAB_LABELS: Record<TabKey, string> = {
  all: 'All', unread: 'Unread', acknowledged: 'Acknowledged', pending_ack: 'Pending Ack',
};

// ── Notice card ───────────────────────────────────────────────────────────────

function NoticeCard({ notice, onClick }: { notice: NoticeWithMeta; onClick: () => void }) {
  const isRead = !!notice.my_read_at;
  const excerpt = notice.content.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 120);

  return (
    <button
      type="button"
      onClick={onClick}
      className={`group text-left w-full rounded-xl border bg-card shadow-sm transition-all duration-200
        hover:shadow-md hover:-translate-y-0.5
        ${isRead ? 'border-border/50 opacity-90' : 'border-l-4 border-l-primary border-border/60'}
      `}
    >
      <div className="p-4 sm:p-5 flex flex-col gap-3">
        {/* Top row */}
        <div className="flex items-start justify-between gap-2">
          <Badge
            variant="outline"
            className={`text-[10px] px-2 py-0.5 rounded-full font-medium border ${PRIORITY_STYLES[notice.priority]}`}
          >
            {PRIORITY_LABELS[notice.priority]}
          </Badge>
          <Badge
            variant="outline"
            className={`text-[10px] px-2 py-0.5 rounded-full font-medium border ${TYPE_STYLES[notice.notice_type]}`}
          >
            {TYPE_LABELS[notice.notice_type]}
          </Badge>
        </div>

        {/* Title */}
        <div>
          <p className="text-sm font-semibold text-foreground line-clamp-2 leading-snug">
            {notice.title}
          </p>
          {excerpt && (
            <p className="mt-1 text-xs text-muted-foreground line-clamp-2 leading-relaxed">
              {excerpt}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-border/30">
          <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
            <Calendar className="h-3 w-3" />
            {formatDate(notice.published_at)}
          </span>
          {notice.requires_acknowledgment && (
            <Badge variant="outline" className="text-[10px] px-2 py-0.5 bg-violet-50 text-violet-700 border-violet-200">
              Req. Acknowledgment
            </Badge>
          )}
          <span className="ml-auto flex items-center gap-1 text-[11px]">
            {isRead
              ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
              : <Eye className="h-3.5 w-3.5 text-muted-foreground" />
            }
            <span className={isRead ? 'text-emerald-600 font-medium' : 'text-muted-foreground'}>
              {isRead ? 'Read' : 'Unread'}
            </span>
          </span>
        </div>
      </div>
    </button>
  );
}

// ── Detail dialog ─────────────────────────────────────────────────────────────

function NoticeDetailDialog({
  notice,
  onClose,
  onRead,
  onAcknowledge,
  isReadPending,
  isAckPending,
}: {
  notice: NoticeWithMeta;
  onClose: () => void;
  onRead: () => void;
  onAcknowledge: () => void;
  isReadPending: boolean;
  isAckPending: boolean;
}) {
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className="text-xs font-mono text-muted-foreground">{notice.notice_number}</span>
            <Badge
              variant="outline"
              className={`text-[10px] px-2 py-0.5 rounded-full border ${PRIORITY_STYLES[notice.priority]}`}
            >
              {PRIORITY_LABELS[notice.priority]}
            </Badge>
            <Badge
              variant="outline"
              className={`text-[10px] px-2 py-0.5 rounded-full border ${TYPE_STYLES[notice.notice_type]}`}
            >
              {TYPE_LABELS[notice.notice_type]}
            </Badge>
            {notice.requires_acknowledgment && (
              <Badge variant="outline" className="text-[10px] px-2 py-0.5 bg-violet-50 text-violet-700 border-violet-200">
                Req. Acknowledgment
              </Badge>
            )}
          </div>
          <DialogTitle className="text-base font-semibold leading-snug">{notice.title}</DialogTitle>
          <DialogDescription asChild>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground mt-2">
              <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> Published: {formatDateTime(notice.published_at)}</span>
              {notice.expires_at && (
                <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> Expires: {formatDateTime(notice.expires_at)}</span>
              )}
              {notice.created_by_name && (
                <span className="flex items-center gap-1"><Users className="h-3 w-3" /> By: {notice.created_by_name}</span>
              )}
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 rounded-lg border border-border/30 bg-background/60 p-4">
          <RichTextContent html={notice.content} />
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 pt-2">
          {!notice.my_read_at && (
            <Button
              size="sm"
              variant="outline"
              onClick={onRead}
              disabled={isReadPending}
              className="h-8 text-xs"
            >
              {isReadPending && <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />}
              <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />
              Mark as Read
            </Button>
          )}
          {notice.requires_acknowledgment && !notice.my_acknowledged_at && (
            <Button
              size="sm"
              onClick={onAcknowledge}
              disabled={isAckPending}
              className="h-8 text-xs bg-teal-600 hover:bg-teal-700 text-white"
            >
              {isAckPending && <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />}
              <BookOpen className="mr-1.5 h-3.5 w-3.5" />
              Acknowledge
            </Button>
          )}
          {notice.my_acknowledged_at && (
            <span className="flex items-center gap-1.5 text-xs text-emerald-600 font-medium">
              <CheckCircle2 className="h-3.5 w-3.5" /> Acknowledged {formatDate(notice.my_acknowledged_at)}
            </span>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function NoticePage() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabKey>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [q, setQ] = useState('');
  const [openNotice, setOpenNotice] = useState<NoticeWithMeta | null>(null);

  const statsQuery = useQuery({
    queryKey: ['notices', 'my', 'stats'],
    queryFn: () => noticeService.myStats(),
  });

  const listQuery = useQuery({
    queryKey: ['notices', 'my', { tab: activeTab, type: typeFilter, q }],
    queryFn: () =>
      noticeService.listMy({
        page: 1,
        limit: 50,
        notice_type: typeFilter !== 'all' ? (typeFilter as NoticeType) : undefined,
        q: q || undefined,
      }),
  });

  const readMutation = useMutation({
    mutationFn: (id: string) => noticeService.markRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notices', 'my'] });
      if (openNotice) setOpenNotice({ ...openNotice, my_read_at: new Date().toISOString() });
      toast.success('Marked as read');
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const ackMutation = useMutation({
    mutationFn: (id: string) => noticeService.acknowledge(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notices', 'my'] });
      if (openNotice) {
        setOpenNotice({
          ...openNotice,
          my_acknowledged_at: new Date().toISOString(),
          my_read_at: openNotice.my_read_at ?? new Date().toISOString(),
        });
      }
      toast.success('Notice acknowledged');
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const stats = statsQuery.data;

  // Client-side tab filter applied on top of server results
  const allItems = listQuery.data?.data ?? [];
  const filtered = allItems.filter((n) => {
    if (activeTab === 'unread') return !n.my_read_at;
    if (activeTab === 'acknowledged') return !!n.my_acknowledged_at;
    if (activeTab === 'pending_ack') return n.requires_acknowledgment && !n.my_acknowledged_at;
    return true;
  });

  return (
    <div className="space-y-5">
      {/* Page header */}
      <div>
        <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">Notices</h1>
        <p className="text-sm text-muted-foreground">Company announcements and updates</p>
      </div>

      {/* Stat cards */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Total"
          value={statsQuery.isLoading ? '—' : (stats?.total ?? 0)}
          icon={Bell}
          tone="sky"
        />
        <StatCard
          label="Unread"
          value={statsQuery.isLoading ? '—' : (stats?.unread ?? 0)}
          icon={Eye}
          tone="amber"
        />
        <StatCard
          label="Acknowledged"
          value={statsQuery.isLoading ? '—' : (stats?.acknowledged ?? 0)}
          icon={CheckCircle2}
          tone="emerald"
        />
        <StatCard
          label="Pending Ack"
          value={statsQuery.isLoading ? '—' : (stats?.pending_ack ?? 0)}
          icon={AlertTriangle}
          tone="rose"
        />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Tab strip */}
        <Tabs
          tabs={ALL_TABS.map((tab) => ({ key: tab, label: TAB_LABELS[tab] }))}
          active={activeTab}
          onChange={(k) => setActiveTab(k as TabKey)}
        />

        {/* Type select */}
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="h-8 w-full sm:w-[160px] text-xs">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="general">General</SelectItem>
            <SelectItem value="urgent">Urgent</SelectItem>
            <SelectItem value="policy">Policy</SelectItem>
            <SelectItem value="event">Event</SelectItem>
            <SelectItem value="circular">Circular</SelectItem>
          </SelectContent>
        </Select>

        {/* Search */}
        <div className="sm:ml-auto sm:w-[220px]">
          <SearchInput
            value={q}
            onChange={(v) => setQ(v)}
            placeholder="Search notices…"
            className="h-8 text-xs"
          />
        </div>
      </div>

      {/* Notice grid */}
      {listQuery.isLoading ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-40 rounded-xl bg-muted/40 animate-pulse" />
          ))}
        </div>
      ) : listQuery.isError ? (
        <div className="rounded-xl border border-border/60 bg-card p-8 text-center">
          <AlertTriangle className="mx-auto h-8 w-8 text-rose-400" />
          <p className="mt-2 text-sm text-muted-foreground">Failed to load notices</p>
          <Button
            size="sm"
            variant="outline"
            className="mt-3 h-8 text-xs"
            onClick={() => listQuery.refetch()}
          >
            Retry
          </Button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-border/60 bg-card p-10 text-center">
          <Bell className="mx-auto h-10 w-10 text-muted-foreground/40" />
          <p className="mt-3 text-sm font-medium text-foreground">No notices found</p>
          <p className="mt-1 text-xs text-muted-foreground">
            {activeTab !== 'all' ? 'Try switching to "All" tab' : 'Check back later for announcements'}
          </p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((notice) => (
            <NoticeCard
              key={notice.id}
              notice={notice}
              onClick={() => setOpenNotice(notice)}
            />
          ))}
        </div>
      )}

      {/* Detail dialog */}
      {openNotice && (
        <NoticeDetailDialog
          notice={openNotice}
          onClose={() => setOpenNotice(null)}
          onRead={() => readMutation.mutate(openNotice.id)}
          onAcknowledge={() => ackMutation.mutate(openNotice.id)}
          isReadPending={readMutation.isPending}
          isAckPending={ackMutation.isPending}
        />
      )}
    </div>
  );
}
